import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Container, Input, Button, VStack, HStack, Heading, useToast, FormControl, FormLabel, 
  IconButton, Select, Checkbox, CheckboxGroup, Stack, Box, Divider, Text,
  Tabs, TabList, TabPanels, Tab, TabPanel, Card, CardHeader, CardBody,
  Badge, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, Flex
} from '@chakra-ui/react'
import { FiPlus, FiTrash } from 'react-icons/fi'
import apiClient from '../../../../utils/apiClient'

const CreateContentModel = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = React.useRef()

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0] || null

  // State for taxonomies
  const [availableTaxonomies, setAvailableTaxonomies] = useState([])
  const [loadingTaxonomies, setLoadingTaxonomies] = useState(false)
  const [fieldToDelete, setFieldToDelete] = useState(null)

  // Form state
  const [model, setModel] = useState({
    tenant_id: tenantId,
    name: '',
    description: '',
    fields: [{ name: '', type: 'text' }], // Default with one field
    taxonomy_ids: [] // New field for taxonomy IDs
  })

  // Fetch available taxonomies when component mounts
  useEffect(() => {
    const fetchTaxonomies = async () => {
      if (!tenantId) return;
      
      try {
        setLoadingTaxonomies(true);
        const response = await apiClient(`/taxonomies/${tenantId}`);
        
        // Handle different response formats
        if (response) {
          if (Array.isArray(response)) {
            // Direct array response
            setAvailableTaxonomies(response);
          } else if (response.taxonomies && Array.isArray(response.taxonomies)) {
            // Object with taxonomies property
            setAvailableTaxonomies(response.taxonomies);
          } else {
            console.error('Unexpected taxonomy response format:', response);
            setAvailableTaxonomies([]);
          }
        } else {
          setAvailableTaxonomies([]);
        }
      } catch (error) {
        console.error('Error fetching taxonomies:', error);
        toast({
          title: 'Error',
          description: 'Failed to load taxonomies',
          status: 'error',
          duration: 3000
        });
        setAvailableTaxonomies([]);
      } finally {
        setLoadingTaxonomies(false);
      }
    }

    fetchTaxonomies()
  }, [tenantId, toast])

  const handleChange = (e) => {
    setModel((prevModel) => ({
      ...prevModel,
      [e.target.name]: e.target.value
    }))
  }

  const handleFieldChange = (index, key, value) => {
    setModel((prevModel) => ({
      ...prevModel,
      fields: prevModel.fields.map((field, i) =>
        i === index ? { ...field, [key]: value } : field
      )
    }))
  }

  const addField = () => {
    setModel((prevModel) => ({
      ...prevModel,
      fields: [...prevModel.fields, { name: '', type: 'text' }]
    }))
  }

  const confirmDeleteField = (index) => {
    setFieldToDelete(index);
    onOpen();
  }

  const removeField = () => {
    if (fieldToDelete !== null) {
      setModel((prevModel) => ({
        ...prevModel,
        fields: prevModel.fields.filter((_, i) => i !== fieldToDelete)
      }));
      setFieldToDelete(null);
    }
    onClose();
  }

  // Handle taxonomy selection changes
  const handleTaxonomyChange = (selectedTaxonomyIds) => {
    // Convert string IDs to numbers
    const numericIds = selectedTaxonomyIds.map(id => Number(id))
    
    setModel(prevModel => ({
      ...prevModel,
      taxonomy_ids: numericIds
    }))
  }

  const handleSubmit = async () => {
    // Basic validation
    if (!model.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Model name is required',
        status: 'error',
        duration: 3000
      });
      return;
    }

    // Check if any fields exist and have names
    const validFields = model.fields.filter(field => field.name.trim());
    if (validFields.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'At least one field with a name is required',
        status: 'error',
        duration: 3000
      });
      return;
    }
    
    try {
      const payload = {
        ...model,
        fields: validFields // Remove empty fields
      }

      const response = await apiClient('/content-models', {
        method: 'POST',
        body: JSON.stringify(payload)
      })

      toast({
        title: 'Success',
        description: 'Content model created successfully',
        status: 'success',
        duration: 3000
      })

      navigate('/content-models')
      return response.data
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create model',
        status: 'error',
        duration: 3000
      })
    }
  }

  return (
    <Container maxW="container.lg" pb={8}>
      <Card mb={6}>
        <CardHeader>
          <Heading size="md">Create Content Model</Heading>
        </CardHeader>
      </Card>

      <Tabs variant="enclosed" colorScheme="blue" isLazy>
        <TabList mb={4}>
          <Tab>Basic Details</Tab>
          <Tab>Fields</Tab>
          <Tab>Taxonomies</Tab>
        </TabList>

        <TabPanels>
          {/* Basic Details Tab */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Model Information</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={6} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Model Name</FormLabel>
                    <Input 
                      name="name" 
                      value={model.name} 
                      onChange={handleChange} 
                      placeholder="Enter model name" 
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Model Description</FormLabel>
                    <Input 
                      name="description" 
                      value={model.description} 
                      onChange={handleChange} 
                      placeholder="Enter model description" 
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Fields Tab */}
          <TabPanel>
            <Card mb={4}>
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="md">Fields</Heading>
                  <Button 
                    leftIcon={<FiPlus />} 
                    colorScheme="blue" 
                    size="sm" 
                    onClick={addField}
                  >
                    Add Field
                  </Button>
                </HStack>
              </CardHeader>
              <CardBody>
                {Array.isArray(model.fields) && model.fields.length > 0 ? (
                  <VStack spacing={4} align="stretch">
                    {model.fields.map((field, index) => (
                      <Card key={index} variant="outline" borderColor="gray.200">
                        <CardBody>
                          <VStack spacing={3} align="stretch">
                            <FormControl isRequired>
                              <FormLabel>Field Name</FormLabel>
                              <Input 
                                value={field.name} 
                                onChange={(e) => handleFieldChange(index, 'name', e.target.value)} 
                                placeholder="Enter field name"
                              />
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel>Field Type</FormLabel>
                              <Select 
                                value={field.type} 
                                onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                              >
                                <option value="text">Text</option>
                                <option value="richtext">Rich Text</option>
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                                <option value="boolean">Boolean</option>
                                <option value="select">Select</option>
                                <option value="multiselect">Multi Select</option>
                                <option value="link">Link</option>
                                <option value="file">File</option>
                                <option value="tags">Tags</option>
                                <option value="json">JSON</option>
                              </Select>
                            </FormControl>

                            <Flex justify="flex-end">
                              <Button 
                                leftIcon={<FiTrash />} 
                                onClick={() => confirmDeleteField(index)} 
                                colorScheme="red" 
                                variant="outline" 
                                size="sm"
                              >
                                Remove Field
                              </Button>
                            </Flex>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </VStack>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Text>No fields added. Click "Add Field" to add a new field.</Text>
                    <Button mt={4} leftIcon={<FiPlus />} colorScheme="blue" onClick={addField}>Add Field</Button>
                  </Box>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Taxonomies Tab */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Assign Taxonomies</Heading>
              </CardHeader>
              <CardBody>
                {loadingTaxonomies ? (
                  <Text>Loading taxonomies...</Text>
                ) : availableTaxonomies.length === 0 ? (
                  <Text>No taxonomies available. Create taxonomies first.</Text>
                ) : (
                  <VStack spacing={4} align="stretch">
                    <CheckboxGroup 
                      colorScheme="blue" 
                      value={model.taxonomy_ids.map(id => id.toString())} 
                      onChange={handleTaxonomyChange}
                    >
                      <Stack spacing={3} direction="column">
                        {availableTaxonomies.map(tax => (
                          <Checkbox key={tax.id} value={tax.id.toString()}>
                            {tax.name}
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                    
                    <Divider my={2} />
                    
                    <Box>
                      <Text fontWeight="medium" mb={2}>Selected Taxonomies:</Text>
                      <Flex wrap="wrap" gap={2}>
                        {model.taxonomy_ids.length > 0 ? (
                          model.taxonomy_ids.map(id => {
                            const taxonomy = availableTaxonomies.find(t => t.id === id);
                            return taxonomy ? (
                              <Badge key={id} colorScheme="blue" variant="subtle" p={2}>
                                {taxonomy.name}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <Text fontSize="sm" color="gray.500">No taxonomies selected</Text>
                        )}
                      </Flex>
                    </Box>
                  </VStack>
                )}
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Action buttons */}
      <Flex justify="flex-end" mt={6} gap={4}>
        <Button onClick={() => navigate('/content-models')}>Cancel</Button>
        <Button 
          colorScheme="blue" 
          onClick={handleSubmit} 
          isDisabled={!model.name || model.fields.length === 0}
        >
          Create Content Model
        </Button>
      </Flex>

      {/* Confirmation Dialog for field deletion */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Field
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this field? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={removeField} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  )
}

export default CreateContentModel
